// API Key创建成功对话框组件
import React, { useState } from 'react';
import { X, Copy, Check, Eye, EyeOff, Shield } from 'lucide-react';

interface KeySuccessDialogProps {
  isOpen: boolean;
  apiKey: string;
  keyName: string;
  onClose: () => void;
}

const KeySuccessDialog: React.FC<KeySuccessDialogProps> = ({
  isOpen,
  apiKey,
  keyName,
  onClose
}) => {
  const [copied, setCopied] = useState(false);
  const [showKey, setShowKey] = useState(true);

  const copyApiKey = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Failed to copy API Key
    }
  };

  const maskApiKey = (key: string) => {
    if (!showKey) {
      return key.substring(0, 8) + '****' + key.substring(key.length - 4);
    }
    return key;
  };

  const handleClose = () => {
    setCopied(false);
    setShowKey(true);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <Shield className="h-4 w-4 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">API Key Created Successfully!</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* 内容 */}
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            "{keyName}" has been created successfully.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
             <p className="text-sm text-yellow-800">
               Please copy your API key now. You can also copy it later by clicking the copy button on the key card in the API Keys page.
             </p>
           </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your API Key
            </label>
            <div className="flex items-center space-x-2">
              <code className="flex-1 bg-gray-50 px-3 py-2 rounded-lg text-sm font-mono text-gray-700 border">
                {maskApiKey(apiKey)}
              </code>
              <button
                onClick={() => setShowKey(!showKey)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title={showKey ? 'Hide Key' : 'Show Key'}
              >
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              <button
                onClick={copyApiKey}
                className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                title={copied ? 'Copied!' : 'Copy API Key'}
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          <div className="flex justify-center">
             <button
               onClick={handleClose}
               className="bg-gray-900 text-white font-medium py-2 px-4 rounded-lg hover:bg-gray-800 transition duration-200 text-sm"
             >
               Got it
             </button>
           </div>
        </div>
      </div>
    </div>
  );
};

export default KeySuccessDialog;